import { useRef, useState, type DragEvent, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Asset, type AssetKind } from "../api.js";
import {
  KIND_ICON,
  KIND_LABEL,
  humanDuration,
  humanSize,
  uploadAsset,
} from "../lib/assetUpload.js";
import { Button, Card, COLORS, Empty, Input, Page } from "../ui.js";
import { GiriDrivePicker } from "../components/GiriDrivePicker.js";

type Filter = "ALL" | AssetKind;

interface PendingUpload {
  id: string; // local
  file: File;
  progress: number;
  status: "uploading" | "done" | "error";
  errorMsg?: string;
}

export default function Library() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("ALL");
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showDrivePicker, setShowDrivePicker] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["assets", filter, search],
    queryFn: () =>
      api.get<{ items: Asset[]; nextCursor: string | null }>(
        `/assets?${new URLSearchParams({
          ...(filter !== "ALL" ? { kind: filter } : {}),
          ...(search ? { search } : {}),
          take: "60",
        })}`
      ),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.del(`/assets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assets"] }),
  });

  async function handleFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    for (const file of arr) {
      const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setPending((p) => [
        ...p,
        { id: localId, file, progress: 0, status: "uploading" as const },
      ]);
      try {
        await uploadAsset(file, {
          onProgress: (pct) => {
            setPending((p) =>
              p.map((u) => (u.id === localId ? { ...u, progress: pct } : u))
            );
          },
        });
        setPending((p) =>
          p.map((u) => (u.id === localId ? { ...u, status: "done" as const } : u))
        );
        qc.invalidateQueries({ queryKey: ["assets"] });
      } catch (err) {
        setPending((p) =>
          p.map((u) =>
            u.id === localId
              ? { ...u, status: "error" as const, errorMsg: String(err) }
              : u
          )
        );
      }
    }
    // Nettoie après 4s les uploads terminés
    setTimeout(() => {
      setPending((p) => p.filter((u) => u.status === "uploading" || u.status === "error"));
    }, 4000);
  }

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      handleFiles(e.target.files);
      e.target.value = "";
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  }

  return (
    <Page title="Bibliothèque">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        <p style={{ color: COLORS.muted, margin: 0, flex: 1, minWidth: 240 }}>
          Vos médias : vidéos (hébergées sur Bunny.net), audio, PDF, images.
          Réutilisables dans toutes vos Leçons et Pages de vente.
        </p>
        <Button
          onClick={() => setShowDrivePicker(true)}
          style={{ whiteSpace: "nowrap" }}
        >
          ☁️ Importer depuis Giri&nbsp;Drive
        </Button>
      </div>

      {showDrivePicker && (
        <GiriDrivePicker onClose={() => setShowDrivePicker(false)} />
      )}

      {/* Drop zone */}
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragActive ? COLORS.accent : COLORS.border}`,
          borderRadius: 12,
          padding: 32,
          textAlign: "center",
          cursor: "pointer",
          background: dragActive ? "#fdf6ee" : "#fff",
          transition: "all 0.15s",
          marginBottom: 20,
        }}
      >
        <div style={{ fontSize: 32, color: COLORS.muted, marginBottom: 8 }}>↑</div>
        <div style={{ fontSize: 16, fontWeight: 600 }}>
          {dragActive ? "Déposez vos fichiers ici" : "Glissez-déposez ou cliquez pour uploader"}
        </div>
        <div style={{ fontSize: 13, color: COLORS.muted, marginTop: 6 }}>
          Vidéo · Audio · PDF · Image
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={onPick}
          style={{ display: "none" }}
          accept="video/*,audio/*,image/*,application/pdf"
        />
      </div>

      {/* Pending uploads */}
      {pending.length > 0 && (
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 12 }}>
            Uploads en cours
          </div>
          {pending.map((u) => (
            <div key={u.id} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                <span>{u.file.name}</span>
                <span style={{ color: u.status === "error" ? COLORS.danger : COLORS.muted }}>
                  {u.status === "error"
                    ? "Erreur"
                    : u.status === "done"
                      ? "✓ Terminé"
                      : `${Math.round(u.progress * 100)} %`}
                </span>
              </div>
              <div
                style={{
                  marginTop: 4,
                  height: 4,
                  background: "#eee",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width:
                      u.status === "done" ? "100%" : `${Math.round(u.progress * 100)}%`,
                    height: "100%",
                    background: u.status === "error" ? COLORS.danger : COLORS.ok,
                    transition: "width 0.2s",
                  }}
                />
              </div>
              {u.errorMsg && (
                <div style={{ fontSize: 12, color: COLORS.danger, marginTop: 4 }}>
                  {u.errorMsg}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 16,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {(["ALL", "VIDEO", "AUDIO", "PDF", "IMAGE"] as const).map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            style={{
              padding: "6px 14px",
              border: `1px solid ${filter === k ? COLORS.accent : COLORS.border}`,
              borderRadius: 999,
              background: filter === k ? COLORS.accent : "transparent",
              color: filter === k ? "#fff" : COLORS.text,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "inherit",
            }}
          >
            {k === "ALL" ? "Tous" : KIND_LABEL[k]}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher…"
          style={{ width: 220 }}
        />
      </div>

      {/* Grid */}
      {isLoading && <Empty>Chargement…</Empty>}
      {data?.items.length === 0 && <Empty>Aucun média. Uploadez votre premier fichier.</Empty>}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 16,
        }}
      >
        {data?.items.map((a) => (
          <AssetCard key={a.id} asset={a} onDelete={() => del.mutate(a.id)} />
        ))}
      </div>
    </Page>
  );
}

function AssetCard({ asset, onDelete }: { asset: Asset; onDelete: () => void }) {
  const isImage = asset.kind === "IMAGE";
  const isVideo = asset.kind === "VIDEO";

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          height: 120,
          background: "#1a1a1a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 36,
          position: "relative",
          ...(isImage && asset.cdnUrl
            ? {
                background: `#000 url(${asset.cdnUrl}) center/cover`,
              }
            : {}),
          ...(isVideo && asset.thumbnailUrl
            ? {
                background: `#000 url(${asset.thumbnailUrl}) center/cover`,
              }
            : {}),
        }}
      >
        {!isImage && !isVideo && KIND_ICON[asset.kind]}
        {!asset.isReady && (
          <div
            style={{
              position: "absolute",
              top: 8,
              left: 8,
              padding: "2px 8px",
              background: "#fef3c7",
              color: "#92400e",
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            En cours…
          </div>
        )}
      </div>
      <div style={{ padding: 12, flex: 1, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={asset.name}
        >
          {asset.name}
        </div>
        <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
          {KIND_LABEL[asset.kind]} · {humanSize(asset.sizeBytes)}
          {asset.durationSec !== null && ` · ${humanDuration(asset.durationSec)}`}
        </div>
        <div style={{ marginTop: 8 }}>
          <Button
            variant="danger"
            onClick={() => {
              if (confirm(`Supprimer "${asset.name}" ?`)) onDelete();
            }}
            style={{ padding: "4px 10px", fontSize: 12 }}
          >
            Supprimer
          </Button>
        </div>
      </div>
    </div>
  );
}
